import axios from 'axios'
import { retryWithBackoff, debounce } from '@/utils/rateLimit'

const state = {
  overview: {
    today: {
      sales: 0,
      costs: 0,
      profit: 0,
      transactionCount: 0
    },
    thisMonth: {
      sales: 0,
      costs: 0,
      profit: 0
    },
    recentTransactions: []
  },
  dailyTotals: [],
  monthlySummary: null,
  weeklySummary: null,
  yearlySummary: null,
  topCategories: [],
  loading: false,
  error: null,
  // Add cache for API responses
  cache: {
    overview: { data: null, timestamp: null, ttl: 5 * 60 * 1000 }, // 5 minutes
    dailyTotals: { data: null, timestamp: null, ttl: 2 * 60 * 1000 }, // 2 minutes
    monthlySummary: { data: null, timestamp: null, ttl: 10 * 60 * 1000 }, // 10 minutes
    weeklySummary: { data: null, timestamp: null, ttl: 5 * 60 * 1000 }, // 5 minutes
    yearlySummary: { data: null, timestamp: null, ttl: 30 * 60 * 1000 }, // 30 minutes
    topCategories: { data: null, timestamp: null, ttl: 15 * 60 * 1000 } // 15 minutes
  }
}

const mutations = {
  SET_OVERVIEW(state, overview) {
    state.overview = overview
    // Cache the data
    state.cache.overview = {
      data: overview,
      timestamp: Date.now(),
      ttl: state.cache.overview.ttl
    }
  },
  SET_DAILY_TOTALS(state, dailyTotals) {
    state.dailyTotals = dailyTotals
    // Cache the data
    state.cache.dailyTotals = {
      data: dailyTotals,
      timestamp: Date.now(),
      ttl: state.cache.dailyTotals.ttl
    }
  },
  SET_MONTHLY_SUMMARY(state, monthlySummary) {
    state.monthlySummary = monthlySummary
    // Cache the data
    state.cache.monthlySummary = {
      data: monthlySummary,
      timestamp: Date.now(),
      ttl: state.cache.monthlySummary.ttl
    }
  },
  SET_WEEKLY_SUMMARY(state, weeklySummary) {
    state.weeklySummary = weeklySummary
    // Cache the data
    state.cache.weeklySummary = {
      data: weeklySummary,
      timestamp: Date.now(),
      ttl: state.cache.weeklySummary.ttl
    }
  },
  SET_YEARLY_SUMMARY(state, yearlySummary) {
    state.yearlySummary = yearlySummary
    // Cache the data
    state.cache.yearlySummary = {
      data: yearlySummary,
      timestamp: Date.now(),
      ttl: state.cache.yearlySummary.ttl
    }
  },
  SET_TOP_CATEGORIES(state, topCategories) {
    state.topCategories = topCategories
    // Cache the data
    state.cache.topCategories = {
      data: topCategories,
      timestamp: Date.now(),
      ttl: state.cache.topCategories.ttl
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_CACHE(state, cacheKey = null) {
    if (cacheKey) {
      if (state.cache[cacheKey]) {
        state.cache[cacheKey] = { data: null, timestamp: null, ttl: state.cache[cacheKey].ttl }
      }
    } else {
      // Clear all cache
      Object.keys(state.cache).forEach(key => {
        state.cache[key] = { data: null, timestamp: null, ttl: state.cache[key].ttl }
      })
    }
  }
}

const actions = {
  async fetchOverview({ commit, state }) {
    // Check cache first
    const cache = state.cache.overview
    if (cache.data && cache.timestamp && (Date.now() - cache.timestamp < cache.ttl)) {
      console.log('📋 Using cached overview data')
      commit('SET_OVERVIEW', cache.data)
      return cache.data
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await retryWithBackoff(async () => {
        return await axios.get('/dashboard/overview')
      })
      commit('SET_OVERVIEW', response.data)
      return response.data
    } catch (error) {
      console.error('Fetch overview error:', error)
      
      // Handle errors (no authentication required)
      console.log('❌ Error in fetchOverview:', error.message)
      
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch overview')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchDailyTotals({ commit, state }, { startDate, endDate }) {
    // Check cache first
    const cache = state.cache.dailyTotals
    if (cache.data && cache.timestamp && (Date.now() - cache.timestamp < cache.ttl)) {
      console.log('📋 Using cached daily totals data')
      commit('SET_DAILY_TOTALS', cache.data)
      return cache.data
    }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await retryWithBackoff(async () => {
        return await axios.get('/dashboard/daily-totals', {
          params: { startDate, endDate }
        })
      })
      commit('SET_DAILY_TOTALS', response.data)
      return response.data
    } catch (error) {
      console.error('Fetch daily totals error:', error)
      
      // Handle errors (no authentication required)
      console.log('❌ Error in fetchDailyTotals:', error.message)
      
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch daily totals')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Debounced version for frequent calls
  async fetchDailyTotalsDebounced({ dispatch }, { startDate, endDate }) {
    return await debounce('dailyTotals', () => 
      dispatch('fetchDailyTotals', { startDate, endDate }), 300
    )
  },

  async fetchMonthlySummary({ commit }, { year, month }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.get('/dashboard/monthly-summary', {
        params: { year, month }
      })
      commit('SET_MONTHLY_SUMMARY', response.data)
    } catch (error) {
      console.error('Fetch monthly summary error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch monthly summary')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchWeeklySummary({ commit }, { startDate } = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.get('/dashboard/weekly-summary', {
        params: startDate ? { startDate } : {}
      })
      commit('SET_WEEKLY_SUMMARY', response.data)
    } catch (error) {
      console.error('Fetch weekly summary error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch weekly summary')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchYearlySummary({ commit }, { year } = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.get('/dashboard/yearly-summary', {
        params: year ? { year } : {}
      })
      commit('SET_YEARLY_SUMMARY', response.data)
    } catch (error) {
      console.error('Fetch yearly summary error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch yearly summary')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTopCategories({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
              const response = await axios.get('/dashboard/top-categories', {
        params
      })
      commit('SET_TOP_CATEGORIES', response.data)
    } catch (error) {
      console.error('Fetch top categories error:', error)
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch top categories')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  overview: state => state.overview,
  dailyTotals: state => state.dailyTotals,
  monthlySummary: state => state.monthlySummary,
  weeklySummary: state => state.weeklySummary,
  yearlySummary: state => state.yearlySummary,
  topCategories: state => state.topCategories,
  loading: state => state.loading,
  error: state => state.error,
  
  // Computed getters
  todayProfit: state => state.overview.today.profit,
  monthProfit: state => state.overview.thisMonth.profit,
  hasData: state => state.overview.thisMonth.sales > 0 || state.overview.thisMonth.costs > 0 || state.dailyTotals.length > 0
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 